/**
 * Widget registry — catalog of all available dashboard widget types.
 * Single source of truth for widget metadata, size constraints, and config schemas.
 */

import type { WidgetRegistryEntry, WidgetType } from '@/types/dashboard'

export const WIDGET_REGISTRY: WidgetRegistryEntry[] = [
  {
    type: 'kpi_card',
    name: 'KPI Card',
    description: 'Single metric with trend indicator',
    icon: 'Hash',
    category: 'metrics',
    dataSources: ['overview'],
    sizeConstraints: { minW: 2, minH: 2, maxW: 4, maxH: 3, defaultW: 3, defaultH: 2 },
    configFields: [
      {
        key: 'kpiMetric',
        label: 'Metric',
        type: 'select',
        options: [
          { value: 'totalSpend', label: 'Total Spend' },
          { value: 'totalImpressions', label: 'Impressions' },
          { value: 'totalClicks', label: 'Clicks' },
          { value: 'totalConversations', label: 'Conversations' },
          { value: 'totalLeads', label: 'Leads' },
          { value: 'totalPurchases', label: 'Purchases' },
          { value: 'avgCpc', label: 'Avg CPC' },
          { value: 'avgCtr', label: 'Avg CTR' },
          { value: 'activeCampaigns', label: 'Active Campaigns' },
        ],
        defaultValue: 'totalSpend',
      },
    ],
    supportsGlobalDateRange: true,
  },
  {
    type: 'attribution_funnel',
    name: 'Attribution Funnel',
    description: 'Click-to-WhatsApp conversion pipeline',
    icon: 'Filter',
    category: 'charts',
    dataSources: ['attribution'],
    // minH 4: the segmented funnel body has intrinsic row height (5 stages ≈ 240px)
    // and WidgetWrapper is overflow-visible, so anything shorter spills the card.
    sizeConstraints: { minW: 6, minH: 4, maxW: 12, maxH: 6, defaultW: 12, defaultH: 4 },
    configFields: [],
    supportsGlobalDateRange: true,
  },
  {
    type: 'performance_trends',
    name: 'Performance Trends',
    description: 'Multi-metric time series line chart',
    icon: 'TrendingUp',
    category: 'charts',
    dataSources: ['timeSeries'],
    sizeConstraints: { minW: 4, minH: 3, maxW: 12, maxH: 6, defaultW: 8, defaultH: 4 },
    configFields: [
      {
        key: 'metrics',
        label: 'Metrics',
        type: 'multi-select',
        options: [
          { value: 'spend', label: 'Spend' },
          { value: 'impressions', label: 'Impressions' },
          { value: 'clicks', label: 'Clicks' },
          { value: 'conversations', label: 'Conversations' },
          { value: 'leads', label: 'Leads' },
          { value: 'purchases', label: 'Purchases' },
        ],
        defaultValue: ['spend', 'clicks', 'conversations'],
      },
      {
        key: 'enableArea',
        label: 'Show area fill',
        type: 'toggle',
        defaultValue: false,
      },
    ],
    supportsGlobalDateRange: true,
  },
  {
    type: 'campaign_comparison',
    name: 'Campaign Comparison',
    description: 'Side-by-side campaign performance bars',
    icon: 'BarChart3',
    category: 'charts',
    dataSources: ['campaignPerformance'],
    sizeConstraints: { minW: 4, minH: 3, maxW: 12, maxH: 6, defaultW: 6, defaultH: 4 },
    configFields: [
      {
        key: 'metrics',
        label: 'Metrics',
        type: 'multi-select',
        options: [
          { value: 'spend', label: 'Spend' },
          { value: 'clicks', label: 'Clicks' },
          { value: 'conversations', label: 'Conversations' },
          { value: 'leads', label: 'Leads' },
          { value: 'roas', label: 'ROAS' },
        ],
        defaultValue: ['spend'],
      },
    ],
    supportsGlobalDateRange: true,
  },
  {
    type: 'budget_sankey',
    name: 'Budget Flow',
    description: 'Money flow from budget to outcomes',
    icon: 'GitBranch',
    category: 'charts',
    dataSources: ['campaignPerformance'],
    sizeConstraints: { minW: 6, minH: 4, maxW: 12, maxH: 7, defaultW: 12, defaultH: 5 },
    configFields: [],
    supportsGlobalDateRange: true,
  },
  {
    type: 'demographic_heatmap',
    name: 'Demographic Heatmap',
    description: 'Performance by age group and gender',
    icon: 'Grid3x3',
    category: 'charts',
    // Demographic breakdowns are never synced; the widget renders an honest
    // not-synced state. Hidden from the catalog, kept resolvable for
    // dashboards that already contain one.
    hidden: true,
    dataSources: [],
    sizeConstraints: { minW: 4, minH: 3, maxW: 12, maxH: 6, defaultW: 6, defaultH: 4 },
    configFields: [
      {
        key: 'colorScheme',
        label: 'Color scheme',
        type: 'select',
        options: [
          { value: 'performance', label: 'Performance (RdYlGn)' },
          { value: 'spend', label: 'Spend (Blues)' },
        ],
        defaultValue: 'performance',
      },
    ],
    supportsGlobalDateRange: true,
  },
  {
    type: 'journey_timeline',
    name: 'Journey Timeline',
    description: 'Contact journey from ad click to purchase',
    icon: 'Route',
    category: 'charts',
    dataSources: ['conversions'],
    sizeConstraints: { minW: 6, minH: 3, maxW: 12, maxH: 5, defaultW: 12, defaultH: 3 },
    configFields: [],
    supportsGlobalDateRange: true,
  },
  {
    type: 'campaign_health_radar',
    name: 'Campaign Health',
    description: 'Radar chart of campaign KPI scores',
    icon: 'Radar',
    category: 'charts',
    dataSources: ['campaignHealth'],
    sizeConstraints: { minW: 4, minH: 4, maxW: 8, maxH: 7, defaultW: 6, defaultH: 5 },
    configFields: [],
    supportsGlobalDateRange: true,
  },
  {
    type: 'spend_roas_scatter',
    name: 'Spend vs. ROAS',
    description: 'Scatter plot of spend efficiency per campaign',
    icon: 'ScatterChart',
    category: 'charts',
    dataSources: ['spendRoas'],
    sizeConstraints: { minW: 4, minH: 3, maxW: 12, maxH: 6, defaultW: 6, defaultH: 5 },
    configFields: [],
    supportsGlobalDateRange: true,
  },
  {
    type: 'active_campaigns_list',
    name: 'Active Campaigns',
    description: 'List of currently running campaigns',
    icon: 'Megaphone',
    category: 'tables',
    dataSources: ['campaigns'],
    sizeConstraints: { minW: 4, minH: 3, maxW: 12, maxH: 8, defaultW: 6, defaultH: 4 },
    configFields: [],
    supportsGlobalDateRange: false,
  },
  {
    type: 'optimization_actions',
    name: 'Optimization Actions',
    description: 'Pending AI optimization recommendations',
    icon: 'Sparkles',
    category: 'insights',
    dataSources: ['optimizationActions'],
    sizeConstraints: { minW: 4, minH: 3, maxW: 12, maxH: 6, defaultW: 6, defaultH: 4 },
    configFields: [],
    supportsGlobalDateRange: false,
  },
  {
    type: 'campaign_performance_table',
    name: 'Performance Table',
    description: 'Detailed campaign metrics table',
    icon: 'Table',
    category: 'tables',
    dataSources: ['campaignPerformance'],
    sizeConstraints: { minW: 6, minH: 3, maxW: 12, maxH: 8, defaultW: 12, defaultH: 5 },
    configFields: [],
    supportsGlobalDateRange: true,
  },
]

/** Look up a widget registry entry by type */
export function getWidgetEntry(type: WidgetType): WidgetRegistryEntry {
  const entry = WIDGET_REGISTRY.find((w) => w.type === type)
  if (!entry) throw new Error(`Unknown widget type: ${type}`)
  return entry
}

/** Get catalog-visible widgets grouped by category (hidden entries excluded) */
export function getWidgetsByCategory(): Record<string, WidgetRegistryEntry[]> {
  const groups: Record<string, WidgetRegistryEntry[]> = {}
  for (const entry of WIDGET_REGISTRY) {
    if (entry.hidden) continue
    if (!groups[entry.category]) groups[entry.category] = []
    groups[entry.category].push(entry)
  }
  return groups
}
